import './StyleDropzone.css'
import React, { useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import StringHelper from '../helpers/StringHelper';
import { Row, Col, Button, Progress } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { FilePlus, X } from 'lucide-react';

const baseStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    borderWidth: 2,
    borderRadius: 16,
    borderColor: '#cbd5e1',
    borderStyle: 'dashed',
    backgroundColor: '#fafbfc',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border 0.2s ease, background 0.2s ease',
    gap: 8,
};

const activeStyle = {
    borderColor: '#6366f1',
    backgroundColor: '#eef2ff',
};

const acceptStyle = {
    borderColor: '#6366f1',
    backgroundColor: '#eef2ff',
};

const rejectStyle = {
    borderColor: '#f43f5e',
    backgroundColor: '#fff1f2',
};



function StyledDropzone(props) {
    const [files = [], setFiles] = useState([]);
    const [isUploading = false, setIsUploading] = useState(false);
    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject
    } = useDropzone({
        accept: 'image/*, video/mp4', onDrop: acceptedFiles => {
            setFiles([...files, ...acceptedFiles.map(file => {
                var previewImg = URL.createObjectURL(file)
                const type = StringHelper.toTitleCase(file.type.split("/")[0]);
                file.filetype = type;
                file.properties = { type: file.type };

                if (file.type.indexOf("video/") !== -1) {
                    previewImg = '/cms/assets/svgs/video-file.svg'

                    var video = document.createElement('video');
                    video.preload = 'metadata';

                    video.onloadedmetadata = function () {
                        window.URL.revokeObjectURL(video.src);
                        var duration = video.duration;
                        var width = video.videoWidth;
                        var height = video.videoHeight;

                        file.properties = { ...file.properties, duration: duration, width: width, height: height }
                        // console.log("video.properties" + JSON.stringify(file.properties))
                    }

                    video.src = URL.createObjectURL(file);;
                }
                else {
                    var img = new Image();

                    img.onload = function () {
                        var height = img.height;
                        var width = img.width;

                        file.properties = { ...file.properties, width: width, height: height }
                        // console.log("image.properties" + JSON.stringify(file.properties))
                        // code here to use the dimensions
                    }

                    img.src = URL.createObjectURL(file);
                }

                // console.log("ACCEPTED FILES " + JSON.stringify(file))
                return Object.assign(file, {
                    preview: previewImg,
                    progress: 0
                })
            })]);
        }
    });

    const handleFilesUpload = () => {
        const { handleFilesUpload = () => { } } = props
        setIsUploading(true);
        handleFilesUpload(files);
    }

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
    }), [
        isDragActive,
        isDragAccept,
        isDragReject
    ]);

    const clear = () => {
        setFiles([]);
        setIsUploading(false);
    }

    const finishUpload = () => {
        const { onFinishUpload = () => { } } = props
        onFinishUpload();
    }

    const updateUploadProgress = (file) => {
        const updatedFiles = files.map((f) => {
            if (f.name === file.name) {
                f.progress = Math.round(file.progress);
            }

            return f
        })

        setFiles(updatedFiles);
    }

    const removeFile = (file) => {
        const newFiles = files.filter(f => {
            return file !== f
        })

        setFiles(newFiles);
    }

    const { clearFilesMapper = () => { }, updateUploadProgressMapper = () => { } } = props;
    clearFilesMapper(clear);
    updateUploadProgressMapper(updateUploadProgress);

    const isAllComplete = isUploading && (files.filter((file) => {
        return file.progress < 100
    }).length === 0);

    const renderAddMoreMediaButton = isAllComplete ? (<Button type="primary" htmlType="button" onClick={clear} style={{ margin: "0 8px" }}>Add More Media</Button>) : null;

    const renderActionButton = isAllComplete ? (<Button htmlType="button" onClick={finishUpload}>Done</Button>) : (<Button disabled={(files.length === 0) || isUploading} type="primary" htmlType="button" onClick={handleFilesUpload}>
        Upload
    </Button>);

    const thumbs = files.map(file => {
        const isComplete = file.progress >= 100;
        const actionBtn = isUploading ? (
            <div style={{ width: 120, minHeight: 32 }}>
                <Progress
                    percent={file.progress}
                    size="small"
                    strokeColor="#6366f1"
                    showInfo={true}
                />
            </div>
        ) : (
            <button
                type="button"
                onClick={() => removeFile(file)}
                style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#94a3b8', padding: 4, display: 'flex', alignItems: 'center',
                }}
            >
                <X size={16} />
            </button>
        );

        const isVideo = file.type && file.type.indexOf('video/') !== -1;

        return (
            <div
                key={file.name}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: '1px solid #f1f5f9',
                    backgroundColor: '#fff',
                    marginBottom: 8,
                }}
            >
                {/* Thumbnail */}
                <div style={{
                    width: 48, height: 36, borderRadius: 6,
                    background: '#f1f5f9', flexShrink: 0,
                    overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {file.preview && (
                        isVideo
                            ? <video src={file.preview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                            : <img alt={file.name} src={file.preview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="filename-txt" style={{ fontSize: 13, fontWeight: 500, color: '#0f172a', marginBottom: 2 }}>
                        {file.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {StringHelper.getReadableFileSizeString(file.size)}
                    </div>
                </div>
                {/* Action */}
                {actionBtn}
            </div>
        );
    });

    const renderDropZone = isUploading ? null : (
        <div {...getRootProps({ style })}>
            <input {...getInputProps()} />
            {/* Icon */}
            <div style={{
                width: 56, height: 56, borderRadius: 14,
                background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 4,
            }}>
                <FilePlus size={28} color="#6366f1" />
            </div>
            {/* Primary text */}
            <span style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                Drop files here or click to upload
            </span>
            {/* Hint text */}
            <span style={{ fontSize: 13, color: '#64748b' }}>
                JPG, PNG, GIF, MP4 • Max 1 GB per file
            </span>
        </div>
    );

    return (
        <div style={{ width: '100%' }}>
            {renderDropZone}

            {/* File list */}
            {files.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    {thumbs}
                </div>
            )}

            {/* Action row */}
            {files.length > 0 && (
                <Row justify="end" style={{ marginTop: 12, gap: 8 }}>
                    <Col>{renderAddMoreMediaButton}</Col>
                    <Col>{renderActionButton}</Col>
                </Row>
            )}
        </div>
    );
}

export default StyledDropzone